import React, { useContext, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";

import { ClientForm } from "../../components/forms/ClientForm";
import { CompanyForm } from "../../components/forms/StoreForm";
import { DivisionForm } from "../../components/forms/DivisionForm";
import { TerminalForm } from "../../components/forms/TerminalForm";
import { CorporationForm } from "../../components/forms/CorporationForm";

import { Tree } from "../../components/tree/Tree";
import { TreeNodeModel, EntityType } from "../../components/tree/types";
import Message from "../../components/ui/Message";

import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import { Application } from "../../models/interfaces";
import Loading from "../../components/ui/Loading";

export const ApprovalPage = () => {

  const location = useLocation();
  const application: Application | undefined = location.state?.application;

  const {
    isLoading,
    addApplication,
    updateApplication,
    getApplication,
    approveRequest,
    rejectRequest,
    deleteRequest,
    loadApplications
  } = useContext(ApplicationsContext);

  const [tree, setTree] = useState<TreeNodeModel[]>([]);
  const [selected, setSelected] = useState<TreeNodeModel | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [approveMessage, setApproveMessage] = useState('');
  const [rejectMessage, setRejectMessage] = useState('');
  const [currentApp, setCurrentApp] = useState(0);
  const [action, setAction] = useState('reject');
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const navigate = useNavigate();
  const isApproval = true;

  /* ===========================
     INIT (NEW vs EDIT)
     =========================== */
  useEffect(() => {

    // ✨ EDITAR
    if (application) {

      loadExistingTree(application.APP_ID);

      return;
    }

    // ✨ NUEVO (comportamiento actual)
    const rootClient: TreeNodeModel = {
      id: uuid(),
      type: "CLIENT",
      name: "",
      saved: false,
      children: []
    };

    setTree([rootClient]);
    setSelected(rootClient);

  }, []);

  const loadExistingTree = async (appId: number) => {
    try {
      setIsLoadingTree(true);
      const treeFromApi = await getApplication(appId);
      treeFromApi.entityId = appId.toString();
      setTree([
        treeFromApi
      ]);
      setSelected(treeFromApi);
    } catch (e) {
      console.error(e);
      setErrorMessage("No fue posible cargar la aplicación.");
    } finally {
      setTimeout(() => {
        setIsLoadingTree(false);
      }, 500);

    }
  };

  /* ===========================
     HELPERS
     =========================== */



  const removeError = () => {
    setErrorMessage('');
  };

  const removeSuccessMessage = () => {
    setSuccessMessage('');
    navigate(-1);

  };

  const removeRejectMessage = () => {
    setRejectMessage('');
  };


  const removeApproveMessage = () => {
    setApproveMessage('');
  };

  const confirmApprove = async () => {
    setApproveMessage('');
    const resp = await approveRequest(currentApp.toString());
    if (resp == 'success') {
      setSuccessMessage("La aprobación se realizó correctamente.")
      loadApplications(['PENDING']);
    } else {
      setErrorMessage(resp);
    }
  };

  const rejDecRequest = async (reason: string) => {

    setRejectMessage("");
    let resp;
    if (action == 'reject') {
      resp = await rejectRequest(currentApp.toString(), reason);
    } else {
      resp = await deleteRequest(currentApp.toString(), reason);
    }

    if (resp == 'success') {
      setSuccessMessage(action == 'reject' ? "La solicitud se rechazó correctamente." : "La solicitud se eliminó correctamente.");
      loadApplications(['PENDING']);
    } else {
      setErrorMessage(resp);
    }
  };

  /*const confirmSendToApprove = async () => {
    setConfirmMessage('');
    const result = await approveRequest(tree[0].entityId);
    if(result){
      setSuccessMessage("Solicitud enviada correctamente.")
    }else{
      setErrorMessage(result)
    }
  }*/


  const addNode = (parent: TreeNodeModel) => {
    const nextType: Record<EntityType, EntityType | null> = {
      CLIENT: "CORP_CONTRACT",
      CORP_CONTRACT: "DIV_CONTRACT",
      DIV_CONTRACT: "STORE_CONTRACT",
      STORE_CONTRACT: "DEVICE_CONTRACT",
      DEVICE_CONTRACT: null,
    };

    const type = nextType[parent.type];
    if (!type) return;

    const newNode: TreeNodeModel = {
      id: uuid(),
      type,
      name: "",
      saved: false,
      children: []
    };

    parent.children.push(newNode);
    setTree([...tree]);
    setSelected(newNode);
  };

  /* ===========================
     SAVE NODE
     =========================== */

  // const saveNode = async (updated: TreeNodeModel) => {
  //   try {
  //     const newTree = updateNodeInTree(tree, {
  //       ...updated,
  //       saved: true
  //     });

  //     const root = newTree[0];
  //     let resp;
  //     let entityId;

  //     if (!root.entityId) {
  //       // 🆕 CREAR
  //       [resp, entityId] = await addApplication(root);

  //       if (resp !== "success") {
  //         setErrorMessage(resp);
  //         return;
  //       }

  //       newTree[0].entityId = entityId;
  //     } else {
  //       // ✏️ ACTUALIZAR
  //       resp = await updateApplication(root, root.entityId);

  //       if (resp !== "success") {
  //         setErrorMessage(resp);
  //         return;
  //       }
  //     }

  //     setTree(newTree);
  //     setSelected({ ...updated, saved: true });
  //     setSuccessMessage("Información guardada correctamente.");

  //   } catch (e) {
  //     console.error(e);
  //     setErrorMessage("Error al guardar la información.");
  //   }
  // };

  const updateNodeInTree = (
    nodes: TreeNodeModel[],
    updatedNode: TreeNodeModel
  ): TreeNodeModel[] =>
    nodes.map(node => {
      if (node.id === updatedNode.id) return updatedNode;
      if (node.children.length) {
        return { ...node, children: updateNodeInTree(node.children, updatedNode) };
      }
      return node;
    });

  const deleteNodeRecursive = (
    nodes: TreeNodeModel[],
    targetId: string
  ): TreeNodeModel[] =>
    nodes
      .filter(n => n.id !== targetId)
      .map(n => ({
        ...n,
        children: deleteNodeRecursive(n.children, targetId)
      }));

  const handleDelete = (node: TreeNodeModel) => {
    setTree(prev => deleteNodeRecursive(prev, node.id));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleApprove = async (appId: number) => {
    setCurrentApp(appId);
    setApproveMessage("¿Confirma que desea aprobar este cliente?")

  };
  const handleReject = async (appId: number) => {
    setCurrentApp(appId);
    setRejectMessage("¿Confirma que desea devolver este cliente?")
  };
  const handleDecline = async (appId: number) => {
    setCurrentApp(appId);
    setAction("decline")
    setRejectMessage("¿Confirma que desea eliminar esta solicitud?")
  };




  const hasTerminal = (nodes: TreeNodeModel[]): boolean =>
    nodes.some(n =>
      (n.type === "DEVICE_CONTRACT" && n.saved) ||
      (n.children.length && hasTerminal(n.children))
    );

  /* ===========================
     FORMS
     =========================== */

  const renderForm = () => {
    if (!selected) return null;

    const props = {
      node: selected, onSave: null, onChange: null,
      readOnly: isApproval
    };

    switch (selected.type) {
      case "CLIENT": return <ClientForm key={"CLIENT" + selected.id} {...props} />;
      case "CORP_CONTRACT": return <CorporationForm key={"CORP_CONTRACT" + selected.id} {...props} />;
      case "DIV_CONTRACT": return <DivisionForm key={"DIV_CONTRACT" + selected.id} {...props} />;
      case "STORE_CONTRACT": return <CompanyForm key={"STORE_CONTRACT" + selected.id} {...props} />;
      case "DEVICE_CONTRACT": return <TerminalForm key={"DEVICE_CONTRACT" + selected.id} {...props} />;
      default: return null;
    }
  };

  /* ===========================
     RENDER
     =========================== */

  return (
    <div className="container-fluid vh-100">
      <div className="row h-100">

        {
          isLoadingTree
            ? <Loading></Loading>
            : <>
              {/* ÁRBOL */}
              <div className="col-4 p-0 border-end d-flex flex-column">
                <div className="flex-grow-1 overflow-auto p-3">
                  <Tree
                    data={tree}
                    onSelect={setSelected}
                    onAdd={addNode}
                    onDelete={handleDelete}
                    readonly={isApproval}
                  />

                  <hr />

                  {hasTerminal(tree) && (
                    <div className="d-flex justify-content-between">
                      <button className="btn btn-secondary" onClick={handleBack}>
                        Atrás
                      </button>

                      <div className="btn-group">
                        <button type="button" className="btn btn-success" onClick={() => handleApprove(application.APP_ID)}>Aprobar</button>
                        <button type="button" className="btn btn-success dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
                          <span className="visually-hidden">Toggle Dropdown</span>
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button
                              className="dropdown-item  text-success"
                              onClick={() => handleApprove(application.APP_ID)}
                            >
                              <i className="bi bi-check-circle me-2"></i>
                              Aprobar
                            </button>
                          </li>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <button
                              className="dropdown-item  text-secondary"
                              onClick={() => handleReject(application.APP_ID)}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Rechazar
                            </button>
                          </li>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>

                          <li>
                            <button
                              className="dropdown-item text-danger"
                              onClick={() => handleDecline(application.APP_ID)}
                            >
                              <i className="bi bi-trash me-2"></i>
                              Eliminar
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>


                  )}
                </div>
              </div>

              {/* FORM */}
              <div className="col-8 p-0 d-flex flex-column">
                <div className="flex-grow-1 overflow-auto p-4">
                  {renderForm()}
                </div>
              </div>
            </>
        }
      </div>

      {errorMessage &&
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }

      {successMessage &&
        <Message
          type="success"
          title="Exitoso"
          message={successMessage}
          closeDialog={removeSuccessMessage}
          handleOk={removeSuccessMessage}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }


      {approveMessage &&
        <Message
          type="success"
          title="Confirmar"
          message={approveMessage}
          closeDialog={removeApproveMessage}
          showCancel={true}
          handleCancel={removeApproveMessage}
          handleOk={confirmApprove}
          okText="Confirmar"
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }

      {successMessage &&
        <Message
          type="success"
          title="Exitoso"
          message={successMessage}
          closeDialog={removeSuccessMessage}
          handleOk={removeSuccessMessage}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }

      {rejectMessage &&
        <Message

          type="success"
          title="Confirme"
          message={rejectMessage}
          closeDialog={removeRejectMessage}
          handleOk={rejDecRequest}
          showReasonField={true}
          backgroundColor="rgba(0,0,0,0.4)"
          showCancel={true}
          handleCancel={removeRejectMessage}
        />
      }

    </div>
  );
};
